import React, { useMemo } from "react";
import { CricketTeamLogo } from "../shared/logos";
import {
  PROFILE_FAV_IPL_TEAM_OPTIONS,
  iplTeamLogoUrl,
  type ProfileFavIplSlug,
} from "./profileFavIplTeams";
import {
  PROFILE_FAV_CRICKET_INT_OPTIONS,
  cricketIntLogoUsesWhiteTile,
  cricketIntTeamLogoUrl,
  type ProfileFavCricketIntSlug,
} from "./profileFavCricketIntTeams";

const SECTION_TITLE_CLASS =
  "mb-3 text-center font-anton text-[15px] font-normal not-italic uppercase leading-[12px] tracking-normal text-white [leading-trim:none] sm:mb-3.5";

const SUBHEAD_BAR_CLASS =
  "mb-2 flex h-[20px] min-h-[20px] max-h-[20px] w-full items-center justify-center bg-white/10 text-center font-anton text-[10px] font-normal not-italic uppercase leading-[16px] tracking-normal text-white [leading-trim:none]";

const TILE_BTN_CLASS =
  "flex h-[70px] w-[70px] shrink-0 flex-col items-stretch justify-between gap-0 overflow-hidden rounded-none border-[3px] transition-colors";

export type ProfileFavTeamExtra = {
  /** Canonical slug (dedupe / keys). */
  slug: string;
  /** Display name used for logos + remove toggle. */
  name: string;
  /** Short tile label (falls back to name). */
  label?: string;
};

type Props = {
  selectedIpl: readonly string[];
  selectedInt: readonly string[];
  onToggleIpl: (slug: ProfileFavIplSlug) => void;
  onToggleInt: (slug: ProfileFavCricketIntSlug) => void;
  /** Starred club/franchise sides not in the curated IPL picker. */
  clubExtras?: readonly ProfileFavTeamExtra[];
  /** Starred national sides not in the curated International picker. */
  intExtras?: readonly ProfileFavTeamExtra[];
  /** Remove a starred extra (persists immediately via favourite-teams toggle). */
  onRemoveExtra?: (teamName: string) => void;
};

function ExtraTeamTile({
  extra,
  onRemove,
}: {
  extra: ProfileFavTeamExtra;
  onRemove?: (teamName: string) => void;
}) {
  const label = extra.label || extra.name;
  return (
    <button
      type="button"
      onClick={() => onRemove?.(extra.name)}
      aria-pressed
      aria-label={`Remove ${extra.name}`}
      title="Remove"
      className={`${TILE_BTN_CLASS} border-[#D2FF00] bg-[#8255BE]`}
    >
      <span className="flex min-h-0 flex-1 flex-col items-center justify-center px-1 py-1.5">
        <CricketTeamLogo
          teamName={extra.name}
          width="40px"
          height="40px"
          className="shrink-0 justify-center [&_img]:object-contain"
        />
      </span>
      <span className="line-clamp-2 shrink-0 px-0.5 pb-1 text-center font-anton text-[10px] font-normal uppercase leading-[12px] tracking-normal text-[#D2FF00] [leading-trim:none]">
        {label}
      </span>
    </button>
  );
}

export function ProfileFavouriteCricketTeamsGrid({
  selectedIpl,
  selectedInt,
  onToggleIpl,
  onToggleInt,
  clubExtras = [],
  intExtras = [],
  onRemoveExtra,
}: Props) {
  const iplSet = useMemo(() => new Set(selectedIpl), [selectedIpl]);
  const intSet = useMemo(() => new Set(selectedInt), [selectedInt]);

  return (
    <div className="w-full min-w-0">
      <p className={SECTION_TITLE_CLASS}>Favourite Cricket teams</p>

      <div className={SUBHEAD_BAR_CLASS}>Club teams</div>
      <div className="grid grid-cols-5 justify-items-center gap-2 sm:gap-2.5">
        {PROFILE_FAV_IPL_TEAM_OPTIONS.map(({ slug, label }) => {
          const isSelected = iplSet.has(slug);
          return (
            <button
              key={slug}
              type="button"
              onClick={() => onToggleIpl(slug)}
              aria-pressed={isSelected}
              aria-label={label}
              className={`${TILE_BTN_CLASS} ${
                isSelected
                  ? "border-[#D2FF00] bg-[#8255BE]"
                  : "border-transparent bg-white/10 hover:bg-white/[0.14]"
              }`}
            >
              <span className="flex min-h-0 flex-1 flex-col items-center justify-center px-1 py-1.5">
                <img
                  src={iplTeamLogoUrl(slug)}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span
                className={`shrink-0 px-0.5 pb-1 text-center font-anton text-[10px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none] line-clamp-2 ${
                  isSelected ? "text-[#D2FF00]" : "text-white"
                }`}
              >
                {label}
              </span>
            </button>
          );
        })}
        {clubExtras.map((extra) => (
          <ExtraTeamTile key={`club-${extra.slug}`} extra={extra} onRemove={onRemoveExtra} />
        ))}
      </div>

      <div className={`${SUBHEAD_BAR_CLASS} mt-4`}>International</div>
      <div className="grid grid-cols-5 justify-items-center gap-2 sm:gap-2.5">
        {PROFILE_FAV_CRICKET_INT_OPTIONS.map(({ slug, label }) => {
          const isSelected = intSet.has(slug);
          return (
            <button
              key={slug}
              type="button"
              onClick={() => onToggleInt(slug)}
              aria-pressed={isSelected}
              aria-label={label}
              className={`${TILE_BTN_CLASS} ${
                isSelected
                  ? "border-[#D2FF00] bg-[#8255BE]"
                  : "border-transparent bg-white/10 hover:bg-white/[0.14]"
              }`}
            >
              <span className="flex min-h-0 flex-1 flex-col items-center justify-center px-1 py-1.5">
                <span
                  className={
                    cricketIntLogoUsesWhiteTile(slug)
                      ? "flex max-h-full max-w-full items-center justify-center rounded-none bg-white p-0.5"
                      : "flex max-h-full max-w-full items-center justify-center"
                  }
                >
                  <img
                    src={cricketIntTeamLogoUrl(slug)}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
              </span>
              <span
                className={`shrink-0 px-0.5 pb-1 text-center font-anton text-[10px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none] line-clamp-2 ${
                  isSelected ? "text-[#D2FF00]" : "text-white"
                }`}
              >
                {label}
              </span>
            </button>
          );
        })}
        {intExtras.map((extra) => (
          <ExtraTeamTile key={`int-${extra.slug}`} extra={extra} onRemove={onRemoveExtra} />
        ))}
      </div>
    </div>
  );
}
